import { useFieldArray, useFormContext, Controller } from 'react-hook-form';
import {
  Grid,
  Box,
  MenuItem,
  Typography,
  Divider,
  Button,
  IconButton,
  Chip,
  Autocomplete,
  TextField,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CustomTextInput from 'components/Forms/CustomTextInput';
import LabelRequired from 'components/FormFields/LabelRequired';
import PromotionActionFields, {
  LazyAutocomplete,
} from 'pages/PosSettings/Promotions/PromotionActionFields';
import {
  PROMOTION_TYPES,
  applyPromotionType,
  ACTION_SCOPES,
} from 'pages/PosSettings/Promotions/promotionAction';
import CouponFields from 'pages/PosSettings/Coupons/CouponFields';
import ChannelSelect from 'pages/PosSettings/ChannelSelect';
import {
  PROMOTION_STATUSES,
  CONDITION_TYPES,
  DAY_OPTIONS,
  CUSTOMER_GROUP_OPTIONS,
} from 'pages/PosSettings/hooks/usePosPromotions';

const FIELD_SX = { width: '100%' };
const FORM_PX = 3;

// Text rather than number inputs: a browser blanks a number field when what was
// typed is not a number, which hides the mistake instead of reporting it.
const WHOLE_NUMBER = { inputMode: 'numeric' };
const AMOUNT = { inputMode: 'decimal' };

const SectionTitle = ({ children }) => (
  <Typography
    variant="subtitle2"
    sx={{ fontWeight: 600, mt: 2, mb: 1, px: FORM_PX, color: '#3C4350' }}
  >
    {children}
  </Typography>
);

const ConditionValueFields = ({
  index,
  conditionType,
  control,
  productOptions,
  categoryOptions,
  lockedCategoryIds,
}) => {
  const { t } = useTranslation();

  switch (conditionType) {
    case 'MIN_ORDER_AMOUNT':
      return (
        <Grid item xs={7}>
          <CustomTextInput
            sx={FIELD_SX}
            name={`conditions.${index}.value`}
            inputProps={AMOUNT}
            label={<LabelRequired>{t('Minimum Amount')}</LabelRequired>}
          />
        </Grid>
      );

    case 'MAX_ORDER_AMOUNT':
      return (
        <Grid item xs={7}>
          <CustomTextInput
            sx={FIELD_SX}
            name={`conditions.${index}.value`}
            inputProps={AMOUNT}
            label={<LabelRequired>{t('Maximum Amount')}</LabelRequired>}
          />
        </Grid>
      );

    case 'MIN_ITEM_QUANTITY':
      return (
        <Grid item xs={7}>
          <CustomTextInput
            sx={FIELD_SX}
            name={`conditions.${index}.value`}
            inputProps={WHOLE_NUMBER}
            label={<LabelRequired>{t('Minimum Items')}</LabelRequired>}
          />
        </Grid>
      );

    case 'ITEM_UUIDS':
      return (
        <Grid item xs={7}>
          <Controller
            name={`conditions.${index}.value`}
            control={control}
            render={({ field }) => {
              const selected = productOptions.filter((o) =>
                (field.value || []).includes(o.value)
              );
              return (
                <LazyAutocomplete
                  multiple
                  options={productOptions}
                  groupBy={(option) => option.category || ''}
                  getOptionLabel={(option) => option.label || ''}
                  value={selected}
                  onChange={(_, newVal) =>
                    field.onChange(newVal.map((v) => v.value))
                  }
                  isOptionEqualToValue={(option, val) =>
                    option.value === val.value
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={t('Items')}
                      placeholder={t('Select items')}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, i) => (
                      <Chip
                        label={option.label}
                        size="small"
                        {...getTagProps({ index: i })}
                        key={option.value}
                      />
                    ))
                  }
                />
              );
            }}
          />
        </Grid>
      );

    case 'CATEGORY_UUIDS':
      if (lockedCategoryIds?.length) return null;
      return (
        <Grid item xs={7}>
          <Controller
            name={`conditions.${index}.value`}
            control={control}
            render={({ field }) => {
              const selected = categoryOptions.filter((o) =>
                (field.value || []).includes(o.value)
              );
              return (
                <LazyAutocomplete
                  multiple
                  options={categoryOptions}
                  getOptionLabel={(option) => option.label || ''}
                  value={selected}
                  onChange={(_, newVal) =>
                    field.onChange(newVal.map((v) => v.value))
                  }
                  isOptionEqualToValue={(option, val) =>
                    option.value === val.value
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={t('Categories')}
                      placeholder={t('Select categories')}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, i) => (
                      <Chip
                        label={option.label}
                        size="small"
                        {...getTagProps({ index: i })}
                        key={option.value}
                      />
                    ))
                  }
                />
              );
            }}
          />
        </Grid>
      );

    case 'CUSTOMER_GROUP':
      return (
        <Grid item xs={7}>
          <Controller
            name={`conditions.${index}.value`}
            control={control}
            render={({ field }) => {
              const selected = CUSTOMER_GROUP_OPTIONS.filter((o) =>
                (field.value || []).includes(o.value)
              );
              return (
                <Autocomplete
                  multiple
                  options={CUSTOMER_GROUP_OPTIONS}
                  getOptionLabel={(option) => option.label || ''}
                  value={selected}
                  onChange={(_, newVal) =>
                    field.onChange(newVal.map((v) => v.value))
                  }
                  isOptionEqualToValue={(option, val) =>
                    option.value === val.value
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={t('Customer Groups')}
                      placeholder={t('Select groups')}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, i) => (
                      <Chip
                        label={option.label}
                        size="small"
                        {...getTagProps({ index: i })}
                        key={option.value}
                      />
                    ))
                  }
                />
              );
            }}
          />
        </Grid>
      );

    case 'DAY_OF_WEEK':
      return (
        <Grid item xs={7}>
          <Controller
            name={`conditions.${index}.value`}
            control={control}
            render={({ field }) => {
              const selected = DAY_OPTIONS.filter((o) =>
                (field.value || []).includes(o.value)
              );
              return (
                <Autocomplete
                  multiple
                  options={DAY_OPTIONS}
                  getOptionLabel={(option) => option.label || ''}
                  value={selected}
                  onChange={(_, newVal) =>
                    field.onChange(newVal.map((v) => v.value))
                  }
                  isOptionEqualToValue={(option, val) =>
                    option.value === val.value
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={t('Days')}
                      placeholder={t('Select days')}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, i) => (
                      <Chip
                        label={option.label}
                        size="small"
                        {...getTagProps({ index: i })}
                        key={option.value}
                      />
                    ))
                  }
                />
              );
            }}
          />
        </Grid>
      );

    case 'TIME_RANGE':
      return (
        <>
          <Grid item xs={3.5}>
            <Controller
              name={`conditions.${index}.value`}
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  size="small"
                  type="time"
                  label={t('From')}
                  InputLabelProps={{ shrink: true }}
                  value={field.value?.from || ''}
                  onChange={(e) =>
                    field.onChange({ ...field.value, from: e.target.value })
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={3.5}>
            <Controller
              name={`conditions.${index}.value`}
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  size="small"
                  type="time"
                  label={t('To')}
                  InputLabelProps={{ shrink: true }}
                  value={field.value?.to || ''}
                  onChange={(e) =>
                    field.onChange({ ...field.value, to: e.target.value })
                  }
                />
              )}
            />
          </Grid>
        </>
      );

    case 'FIRST_ORDER':
      return (
        <Grid item xs={7}>
          <Typography variant="body2" sx={{ color: 'text.secondary', pt: 1 }}>
            {t("Applies only to the customer's first order")}
          </Typography>
        </Grid>
      );

    default:
      return null;
  }
};

const getDefaultConditionValue = (type) => {
  switch (type) {
    case 'MIN_ORDER_AMOUNT':
    case 'MAX_ORDER_AMOUNT':
    case 'MIN_ITEM_QUANTITY':
      return '';
    case 'ITEM_UUIDS':
    case 'CATEGORY_UUIDS':
    case 'CUSTOMER_GROUP':
    case 'DAY_OF_WEEK':
      return [];
    case 'TIME_RANGE':
      return { from: '', to: '' };
    case 'FIRST_ORDER':
      return true;
    default:
      return '';
  }
};

const CreateOrEditForm = ({
  productOptions = [],
  categoryOptions = [],
  lockedProductIds,
  lockedCategoryIds,
  lockedProductName,
  lockedCategoryName,
}) => {
  const { t } = useTranslation();
  const { control, watch, setValue } = useFormContext();

  const {
    fields: conditionFields,
    append: appendCondition,
    remove: removeCondition,
  } = useFieldArray({ control, name: 'conditions' });

  const watchType = watch('type');
  const lockedScope =
    lockedProductIds?.length || lockedCategoryIds?.length
      ? ACTION_SCOPES.item
      : undefined;
  const requiresCode = watch('trigger') === 'CODE';

  return (
    <Box sx={{ mt: 1, pb: 2 }}>
      {/* Basic Information */}
      <SectionTitle>{t('Basic Information')}</SectionTitle>
      <Grid container spacing={2} sx={{ px: FORM_PX }}>
        <Grid item xs={6}>
          <CustomTextInput
            sx={FIELD_SX}
            name="name"
            label={<LabelRequired>{t('Name')}</LabelRequired>}
          />
        </Grid>
        <Grid item xs={6}>
          <CustomTextInput
            sx={FIELD_SX}
            select
            name="type"
            label={<LabelRequired>{t('Type')}</LabelRequired>}
            onChange={(e) =>
              applyPromotionType(setValue, e.target.value, lockedScope)
            }
          >
            {PROMOTION_TYPES.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </CustomTextInput>
        </Grid>
        <ChannelSelect />
        <Grid item xs={12}>
          <Controller
            name="trigger"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={field.value === 'CODE'}
                    onChange={(e) =>
                      field.onChange(e.target.checked ? 'CODE' : 'AUTOMATIC')
                    }
                  />
                }
                label={t('Requires coupon code')}
              />
            )}
          />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t(
              'When on, this promotion only applies once the customer supplies one of its coupon codes. When off, it applies automatically.'
            )}
          </Typography>
        </Grid>
      </Grid>

      {/* Coupon — the code this promotion waits for */}
      {requiresCode && (
        <>
          <Divider sx={{ my: 2 }} />
          <SectionTitle>{t('Coupon Code')}</SectionTitle>
          <Grid container spacing={2} sx={{ px: FORM_PX }}>
            <CouponFields />
          </Grid>
        </>
      )}

      {/* Schedule */}
      <Divider sx={{ my: 2 }} />
      <SectionTitle>{t('Schedule')}</SectionTitle>
      <Grid container spacing={2} sx={{ px: FORM_PX }}>
        <Grid item xs={6}>
          <CustomTextInput
            sx={FIELD_SX}
            name="startAt"
            inputProps={{ type: 'date' }}
            label={t('Start Date')}
          />
        </Grid>
        <Grid item xs={6}>
          <CustomTextInput
            sx={FIELD_SX}
            name="endAt"
            inputProps={{ type: 'date' }}
            label={t('End Date')}
          />
        </Grid>
        <Grid item xs={6}>
          <CustomTextInput sx={FIELD_SX} select name="status" label={t('Status')}>
            {PROMOTION_STATUSES.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </CustomTextInput>
        </Grid>
      </Grid>

      {/* Action */}
      {watchType && (
        <>
          <Divider sx={{ my: 2 }} />
          <SectionTitle>{t('Action')}</SectionTitle>
          <Grid container spacing={2} sx={{ px: FORM_PX }}>
            <PromotionActionFields
              type={watchType}
              productOptions={productOptions}
              categoryOptions={categoryOptions}
              lockedProductIds={lockedProductIds}
              lockedCategoryIds={lockedCategoryIds}
              lockedProductName={lockedProductName}
              lockedCategoryName={lockedCategoryName}
            />
          </Grid>
        </>
      )}

      {/* Conditions */}
      <Divider sx={{ my: 2 }} />
      <Box sx={{ px: FORM_PX }}>
        <Accordion
          defaultExpanded={false}
          sx={{
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
            '&:before': { display: 'none' },
            bgcolor: '#fff',
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, py: 1 }}>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#141414',
              }}
            >
              {t('Conditions')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                mb: 1,
              }}
            >
              <Button
                size="small"
                startIcon={<AddOutlinedIcon />}
                onClick={() => appendCondition({ type: '', value: '' })}
              >
                {t('Add')}
              </Button>
            </Box>
            {conditionFields.map((field, index) => {
              const conditionType = watch(`conditions.${index}.type`);
              return (
                <Grid
                  container
                  spacing={2}
                  key={field.id}
                  sx={{ mb: 0, mt: 0 }}
                  alignItems="flex-start"
                >
                  <Grid item xs={4}>
                    <CustomTextInput
                      sx={FIELD_SX}
                      select
                      name={`conditions.${index}.type`}
                      label={t('Condition')}
                      onChange={(e) => {
                        const newType = e.target.value;
                        setValue(`conditions.${index}.type`, newType, {
                          shouldDirty: true,
                        });
                        setValue(
                          `conditions.${index}.value`,
                          getDefaultConditionValue(newType),
                          { shouldDirty: true }
                        );
                      }}
                    >
                      {CONDITION_TYPES.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </CustomTextInput>
                  </Grid>
                  <ConditionValueFields
                    index={index}
                    conditionType={conditionType}
                    control={control}
                    productOptions={productOptions}
                    categoryOptions={categoryOptions}
                    lockedCategoryIds={lockedCategoryIds}
                  />
                  <Grid item xs={1} sx={{ pt: '12px !important' }}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeCondition(index)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              );
            })}
            {conditionFields.length === 0 && (
              <Typography
                variant="body2"
                sx={{ py: 1, color: 'text.secondary' }}
              >
                {t('No conditions added')}
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Advanced — last section, collapsed by default; Stackable lives here */}
      <Divider sx={{ my: 2 }} />
      <Box sx={{ px: FORM_PX, pb: 1 }}>
        <Accordion
          defaultExpanded={false}
          sx={{
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
            '&:before': { display: 'none' },
            bgcolor: '#fff',
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, py: 1 }}>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#141414',
              }}
            >
              {t('Advanced')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
            <Controller
              name="stackable"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value || false}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={t('Stackable')}
                />
              )}
            />
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', mt: 0.5 }}
            >
              {t(
                'When on, this promotion can be combined with other promotions on the same order. When off, it cannot be applied together with any other promotion.'
              )}
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
};

export default CreateOrEditForm;
